import { Navigate, Route, Routes } from 'react-router-dom';
import type { Role } from '@motoschool/shared';
import { useAuth } from './auth/AuthContext';
import { usePageViews } from './lib/analytics';
import { Layout } from './components/Layout';
import { PageLoader } from './components/ui';
import { LandingPage } from './pages/LandingPage';
import { CoursePage, COURSES } from './pages/CoursePage';
import { LoginPage } from './pages/LoginPage';
import { EnterByLinkPage } from './pages/EnterByLinkPage';
import { SchedulePublicPage } from './pages/SchedulePublicPage';
import { SharedSchedulePage } from './pages/SharedSchedulePage';
import { SchedulePage } from './pages/SchedulePage';
import { RequestsPage } from './pages/RequestsPage';
import { StudentsPage } from './pages/StudentsPage';
import { StudentDetailPage } from './pages/StudentDetailPage';
import { OverviewPage } from './pages/OverviewPage';
import { StaffPage } from './pages/StaffPage';
import { SettingsPage } from './pages/SettingsPage';
import { WorkingHoursPage } from './pages/WorkingHoursPage';
import { BookLessonPage } from './pages/BookLessonPage';
import { BalancePage } from './pages/BalancePage';
import { ProfilePage } from './pages/ProfilePage';

function RequireRole({
  roles,
  children,
}: {
  roles: Role[];
  children: React.JSX.Element;
}): React.JSX.Element {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) return <Navigate to="/schedule" replace />;
  return children;
}

/**
 * Маршруты для гостя: лендинг, публичное расписание и вход.
 */
export function GuestRoutes(): React.JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      {/* Страницы под запросы: /kategoriya-a, /kategoriya-a1, /s-nulya */}
      {COURSES.map((course) => (
        <Route
          key={course.slug}
          path={`/${course.slug}`}
          element={<CoursePage slug={course.slug} />}
        />
      ))}
      <Route path="/schedule" element={<SchedulePublicPage />} />
      <Route path="/s/:token" element={<SharedSchedulePage />} />
      <Route path="/enter/:token" element={<EnterByLinkPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Маршруты вошедшего пользователя. Адрес /schedule здесь ведёт в личное
 * расписание — деревья маршрутов не пересекаются, поэтому один и тот же
 * понятный адрес работает и для гостя, и для своего.
 */
function AppRoutes(): React.JSX.Element {
  return (
    <Routes>
      {/*
        Главная остаётся главной и после входа: это публичная страница школы,
        и закрывать её от своих незачем — раньше вход на «/» молча перекидывал
        в кабинет, и посмотреть, что видит клиент, было нельзя.
      */}
      <Route path="/" element={<LandingPage />} />

      {/* Страницы курсов открыты и после входа: это витрина школы */}
      {COURSES.map((course) => (
        <Route
          key={course.slug}
          path={`/${course.slug}`}
          element={<CoursePage slug={course.slug} />}
        />
      ))}

      {/* Ссылка инструктора остаётся доступной и после входа */}
      <Route path="/s/:token" element={<SharedSchedulePage />} />

      {/*
        Ссылка на вход нужна и здесь: на общем телефоне по ней заходит
        следующий ученик, и без этого маршрута его бы просто перекинуло
        в чужой уже открытый кабинет.
      */}
      <Route path="/enter/:token" element={<EnterByLinkPage />} />

      <Route element={<Layout />}>
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/profile" element={<ProfilePage />} />

        {/* Разделы инструктора и администратора */}
        <Route
          path="/requests"
          element={
            <RequireRole roles={['TEACHER', 'ADMIN']}>
              <RequestsPage />
            </RequireRole>
          }
        />
        <Route
          path="/students"
          element={
            <RequireRole roles={['TEACHER', 'ADMIN']}>
              <StudentsPage />
            </RequireRole>
          }
        />
        <Route
          path="/students/:id"
          element={
            <RequireRole roles={['TEACHER', 'ADMIN']}>
              <StudentDetailPage />
            </RequireRole>
          }
        />
        <Route
          path="/overview"
          element={
            <RequireRole roles={['TEACHER', 'ADMIN']}>
              <OverviewPage />
            </RequireRole>
          }
        />
        <Route
          path="/staff"
          element={
            <RequireRole roles={['ADMIN']}>
              <StaffPage />
            </RequireRole>
          }
        />
        <Route
          path="/working-hours"
          element={
            <RequireRole roles={['TEACHER', 'ADMIN']}>
              <WorkingHoursPage />
            </RequireRole>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireRole roles={['ADMIN']}>
              <SettingsPage />
            </RequireRole>
          }
        />

        {/* Разделы ученика */}
        <Route path="/book" element={<BookLessonPage />} />
        <Route path="/my-requests" element={<RequestsPage />} />
        <Route path="/balance" element={<BalancePage />} />
      </Route>

      <Route path="*" element={<Navigate to="/schedule" replace />} />
    </Routes>
  );
}

export function App(): React.JSX.Element {
  const { user, isLoading } = useAuth();

  // Переходы внутри SPA документ не перезагружают — просмотры шлём сами
  usePageViews();

  // Пока не знаем, кто перед нами, показывать что-то одно нельзя:
  // иначе вошедший увидит лендинг и мигание на редиректе.
  if (isLoading) return <PageLoader />;

  return user ? <AppRoutes /> : <GuestRoutes />;
}
